import { useState, useEffect, useRef, useMemo } from "react";
import BarraSuperior from "./barra_superior";
import AnimacionCarga from "./animacion_de_carga";
import "../css/animacion_de_carga.css";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart,
  RadialLinearScale,
  PointElement,
  LineElement,
  LineController,
  Filler,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ArcElement,
} from 'chart.js';
import { obtenerRondas } from "../../functions/consultas";
import { crearTitulo, crearGrafica } from "../../functions/gestion";

const AnalisisRondas = () => {
  const [animacionCarga, setCargando] = useState(true);
  const [datosMostrar, setDatosMostrar] = useState(false);
  const [graficaMostrar, setGraficaMostrar] = useState(true);
  const [estadisticas, setEstadisticas] = useState({});
  const hasFetched = useRef(false);

  const inc = (map, key) => key && map.set(key, (map.get(key) || 0) + 1);

  const toObj = (maps) => Object.fromEntries(
    Object.entries(maps).map(([k, map]) => [k, Object.fromEntries(map)])
  );

  const procesarRondas = (data) => {
    const m = {
      porUsuario: new Map(),
      porCliente: new Map(),
      porPuntoQR: new Map(),
      porHora: new Map(),
      porDia: new Map(),
      porMes: new Map(),
      porDiaSemana: new Map(),
      rondasPorDia: new Map(),
      usuariosPorDia: new Map()
    };

    const añoActual = new Date().getFullYear().toString();
    const rondasPorUsuarioYDia = new Map();
    const tiemposEntreRondas = [];
    const rondasOrdenadas = [];

    Object.values(data).forEach(ronda => {
      const { fecha, hora, usuario, cliente, descripcion } = ronda;
      
      if (!fecha || !hora) return;

      if (!fecha.startsWith(añoActual)) return;

      inc(m.porUsuario, usuario);
      inc(m.porCliente, cliente);
      inc(m.porPuntoQR, descripcion);
      inc(m.porHora, hora.substring(0, 2));
      inc(m.porDia, fecha);
      inc(m.porMes, fecha.substring(0, 7));

      const diaSemana = new Date(fecha).getDay();
      const nombresDias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
      inc(m.porDiaSemana, nombresDias[diaSemana]);

      inc(m.rondasPorDia, fecha);

      if (!m.usuariosPorDia.has(fecha)) {
        m.usuariosPorDia.set(fecha, new Set());
      }
      m.usuariosPorDia.get(fecha).add(usuario);

      const keyUsuario = `${usuario}-${fecha}`;
      inc(rondasPorUsuarioYDia, keyUsuario);

      rondasOrdenadas.push({
        fechaHora: new Date(`${fecha}T${hora}`),
        usuario,
        descripcion
      });
    });

    rondasOrdenadas.sort((a, b) => a.fechaHora - b.fechaHora);

    const tiemposPorUsuario = new Map();
    for (let i = 1; i < rondasOrdenadas.length; i++) {
      const actual = rondasOrdenadas[i];
      const anterior = rondasOrdenadas[i - 1];
      
      if (actual.usuario === anterior.usuario) {
        const diferencia = (actual.fechaHora - anterior.fechaHora) / (1000 * 60);
        if (diferencia > 0 && diferencia < 480) {
          tiemposEntreRondas.push(diferencia);
          if (!tiemposPorUsuario.has(actual.usuario)) {
            tiemposPorUsuario.set(actual.usuario, []);
          }
          tiemposPorUsuario.get(actual.usuario).push(diferencia);
        }
      }
    }

    const usuariosPorDiaObj = new Map();
    m.usuariosPorDia.forEach((usuarios, dia) => {
      usuariosPorDiaObj.set(dia, usuarios.size);
    });

    const intervalosPorRango = new Map();
    tiemposEntreRondas.forEach(minutos => {
      let rango;
      if (minutos < 15) rango = '0-15 min';
      else if (minutos < 30) rango = '15-30 min';
      else if (minutos < 60) rango = '30-60 min';
      else if (minutos < 120) rango = '1-2 horas';
      else if (minutos < 240) rango = '2-4 horas';
      else rango = '4+ horas';
      inc(intervalosPorRango, rango);
    });

    const promedioRondasPorUsuario = new Map();
    rondasPorUsuarioYDia.forEach((cantidad, key) => {
      const usuario = key.split('-')[0];
      if (!promedioRondasPorUsuario.has(usuario)) {
        promedioRondasPorUsuario.set(usuario, []);
      }
      promedioRondasPorUsuario.get(usuario).push(cantidad);
    });

    const promediosFinales = new Map();
    promedioRondasPorUsuario.forEach((cantidades, usuario) => {
      const promedio = cantidades.reduce((a, b) => a + b, 0) / cantidades.length;
      promediosFinales.set(usuario, Math.round(promedio * 10) / 10);
    });

    const tiempoPromedioUsuario = new Map();
    tiemposPorUsuario.forEach((tiempos, usuario) => {
      const promedio = tiempos.reduce((a, b) => a + b, 0) / tiempos.length;
      tiempoPromedioUsuario.set(usuario, Math.round(promedio));
    });

    const puntosQRPorUsuario = new Map();
    Object.values(data).forEach(({ usuario, descripcion, fecha }) => {
      if (!fecha?.startsWith(añoActual)) return;
      if (!puntosQRPorUsuario.has(usuario)) {
        puntosQRPorUsuario.set(usuario, new Set());
      }
      puntosQRPorUsuario.get(usuario).add(descripcion);
    });

    const coberturaPuntosQR = new Map();
    puntosQRPorUsuario.forEach((puntos, usuario) => {
      coberturaPuntosQR.set(usuario, puntos.size);
    });

    const rondasPorTurno = new Map();
    Object.values(data).forEach(({ fecha, hora }) => {
      if (!fecha?.startsWith(añoActual) || !hora) return;
      const h = parseInt(hora.substring(0, 2));
      let turno;
      if (h >= 6 && h < 12) turno = 'Mañana (6am-12pm)';
      else if (h >= 12 && h < 18) turno = 'Tarde (12pm-6pm)';
      else if (h >= 18 && h < 24) turno = 'Noche (6pm-12am)';
      else turno = 'Madrugada (12am-6am)';
      inc(rondasPorTurno, turno);
    });

    const fechasUnicas = new Set(Object.values(data)
      .filter(r => r.fecha?.startsWith(añoActual))
      .map(r => r.fecha));
    
    const primerDia = new Date(Math.min(...Array.from(fechasUnicas).map(f => new Date(f))));
    const ultimoDia = new Date(Math.max(...Array.from(fechasUnicas).map(f => new Date(f))));
    const diasTotales = Math.ceil((ultimoDia - primerDia) / (1000 * 60 * 60 * 24)) + 1;
    const diasConRondas = fechasUnicas.size;
    const diasSinRondas = diasTotales - diasConRondas;

    return {
      ...toObj(m),
      usuariosPorDia: Object.fromEntries(usuariosPorDiaObj),
      intervaloEntreRondas: Object.fromEntries(intervalosPorRango),
      promedioRondasPorUsuarioDia: Object.fromEntries(promediosFinales),
      tiempoPromedioEntreRondasPorUsuario: Object.fromEntries(tiempoPromedioUsuario),
      coberturaPuntosQRPorUsuario: Object.fromEntries(coberturaPuntosQR),
      rondasPorTurno: Object.fromEntries(rondasPorTurno),
      metricas: {
        totalRondas: Object.keys(data).length,
        diasConRondas,
        diasSinRondas,
        diasTotales,
        tasaCobertura: Math.round((diasConRondas / diasTotales) * 100),
        promedioRondasDia: Math.round(Object.keys(data).length / diasConRondas),
        tiempoPromedioGeneral: tiemposEntreRondas.length > 0 
          ? Math.round(tiemposEntreRondas.reduce((a, b) => a + b, 0) / tiemposEntreRondas.length)
          : 0
      }
    };
  };
  const capturarDatos = async () => {
    setCargando(true);
    
    try {
      const data = await obtenerRondas();
      
      if (data && Object.keys(data).length > 0) {
        const stats = procesarRondas(data);
        setEstadisticas(stats);
      } else {
        setEstadisticas({});
      }
    } catch (error) {
      console.error("Error capturando datos de rondas:", error);
    } finally {
      setCargando(false);
    }
  };

  const metricasResumen = useMemo(() => {
    const { metricas = {} } = estadisticas;
    
    return [
      { metrica: 'Total de Rondas Realizadas', valor: metricas.totalRondas || 0 },
      { metrica: 'Días con Rondas', valor: metricas.diasConRondas || 0 },
      { metrica: 'Días sin Rondas', valor: metricas.diasSinRondas || 0 },
      { metrica: 'Total de Días Analizados', valor: metricas.diasTotales || 0 },
      { metrica: 'Tasa de Cobertura (%)', valor: metricas.tasaCobertura || 0 },
      { metrica: 'Promedio Rondas por Día', valor: metricas.promedioRondasDia || 0 },
      { metrica: 'Tiempo Promedio entre Rondas (min)', valor: metricas.tiempoPromedioGeneral || 0 }
    ];
  }, [estadisticas]);

  const tablaDesempenoUsuarios = useMemo(() => {
    if (!estadisticas.porUsuario) return [];
    
    const usuarios = Object.keys(estadisticas.porUsuario);
    return usuarios.map(usuario => ({
      usuario,
      totalRondas: estadisticas.porUsuario[usuario] || 0,
      promedioRondasDia: estadisticas.promedioRondasPorUsuarioDia?.[usuario] || 0,
      tiempoPromedio: estadisticas.tiempoPromedioEntreRondasPorUsuario?.[usuario] || 0,
      puntosQRCubiertos: estadisticas.coberturaPuntosQRPorUsuario?.[usuario] || 0
    })).sort((a, b) => b.totalRondas - a.totalRondas);
  }, [estadisticas]);

  const puntosQRAnalisis = useMemo(() => {
    if (!estadisticas.porPuntoQR) return { masVisitados: [], menosVisitados: [] };
    
    const puntos = Object.entries(estadisticas.porPuntoQR)
      .filter(([punto, visitas]) => visitas >= 3)
      .map(([punto, visitas]) => ({ punto, visitas }))
      .sort((a, b) => b.visitas - a.visitas);
    
    return {
      masVisitados: puntos.slice(0, 10),
      menosVisitados: puntos.slice(-10).reverse()
    };
  }, [estadisticas]);

  useEffect(() => {
    Chart.register(
      RadialLinearScale, PointElement, LineElement, LineController,
      Filler, Tooltip, Legend, CategoryScale, LinearScale,
      BarElement, Title, ArcElement, ChartDataLabels
    );
  }, []);

  useEffect(() => {
    if (!hasFetched.current) {
      capturarDatos();
      hasFetched.current = true;
    }
  }, []);

  if (animacionCarga) return <AnimacionCarga />;

  return (
    <div>
      <BarraSuperior />
      <div className="BloqueModulo">
        <br /><br /><br />

        <div style={{ padding: '0px 10px 0px 0px', textAlign: 'right' }}>
          <br />
          <h2>Opciones de visualización</h2>
          <br />
          <label>
            <input type="checkbox" checked={graficaMostrar} onChange={(e) => setGraficaMostrar(e.target.checked)} />
            Mostrar Gráfica de datos
          </label>
          <br />
          <label>
            <input type="checkbox" checked={datosMostrar} onChange={(e) => setDatosMostrar(e.target.checked)} />
            Mostrar Tabla de datos
          </label>
        </div>

        {crearTitulo('Métricas Generales de Desempeño')}
        <br />

        <table id="table" className="tabla-responsive">
          <thead>
            <tr>
              <th>Métrica</th>
              <th>Valor</th>
            </tr>
          </thead>
          <tbody>
            {metricasResumen.map((item, index) => (
              <tr key={index}>
                <td>{item.metrica}</td>
                <td>{item.valor}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <br />

        {tablaDesempenoUsuarios.length > 0 && (
          <>
            {crearTitulo('Desempeño Individual del Personal de Seguridad')}
            
            <table id="table" className="tabla-responsive">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Total Rondas</th>
                  <th>Promedio Rondas/Día</th>
                  <th>Tiempo Promedio entre Rondas (min)</th>
                  <th>Puntos QR Cubiertos</th>
                </tr>
              </thead>
              <tbody>
                {tablaDesempenoUsuarios.map((item, index) => (
                  <tr key={index}>
                    <td>{item.usuario}</td>
                    <td>{item.totalRondas}</td>
                    <td>{item.promedioRondasDia}</td>
                    <td>{item.tiempoPromedio}</td>
                    <td>{item.puntosQRCubiertos}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <br />
            
            {estadisticas.porUsuario && crearGrafica('barra', 'Total de Rondas por Usuario', estadisticas.porUsuario, 500, 1200, "texto", graficaMostrar, datosMostrar)}
          </>
        )}

        {puntosQRAnalisis.masVisitados.length > 0 && (
          <>
            
            {crearTitulo('Top 10 Puntos Más Visitados')}
            <table id="table" className="tabla-responsive">
              <thead>
                <tr>
                  <th>Punto QR</th>
                  <th>Visitas</th>
                </tr>
              </thead>
              <tbody>
                {puntosQRAnalisis.masVisitados.map((item, index) => (
                  <tr key={index}>
                    <td>{item.punto}</td>
                    <td>{item.visitas}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <br />

            {crearGrafica('barra', 'Puntos QR Más Visitados', 
              Object.fromEntries(puntosQRAnalisis.masVisitados.map(p => [p.punto, p.visitas])), 
              500, 1200, "texto", graficaMostrar, datosMostrar)}

            {crearTitulo('Top 10 Puntos Menos Visitados (⚠️ Requieren atención)')}
            <table id="table" className="tabla-responsive">
              <thead>
                <tr>
                  <th>Punto QR</th>
                  <th>Visitas</th>
                </tr>
              </thead>
              <tbody>
                {puntosQRAnalisis.menosVisitados.map((item, index) => (
                  <tr key={index}>
                    <td>{item.punto}</td>
                    <td>{item.visitas}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <br />

            {crearGrafica('barra', 'Puntos QR Menos Visitados (Áreas de Mejora)', 
              Object.fromEntries(puntosQRAnalisis.menosVisitados.map(p => [p.punto, p.visitas])), 
              500, 1200, "texto", graficaMostrar, datosMostrar)}
          </>
        )}

        {estadisticas.porMes && (
          <>
            {crearTitulo('Tendencia Temporal de Rondas')}
            {crearGrafica('barra', 'Evolución Mensual de Rondas', estadisticas.porMes, 500, 1200, "texto", graficaMostrar, datosMostrar)}
          </>
        )}

        {estadisticas.porDiaSemana && (
          <>
            {crearGrafica('radar', 'Distribución por Día de la Semana', estadisticas.porDiaSemana, 600, 600, "texto", graficaMostrar, datosMostrar)}
          </>
        )}

        {estadisticas.rondasPorTurno && (
          <>
            {crearTitulo('Análisis de Turnos y Eficiencia')}
            
            <table id="table" className="tabla-responsive">
              <thead>
                <tr>
                  <th>Turno</th>
                  <th>Rondas Realizadas</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(estadisticas.rondasPorTurno)
                  .sort((a, b) => b[1] - a[1])
                  .map(([turno, cantidad], index) => (
                    <tr key={index}>
                      <td>{turno}</td>
                      <td>{cantidad}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <br />

            {crearGrafica('radar', 'Cobertura por Turno de Trabajo', estadisticas.rondasPorTurno, 600, 600, "texto", graficaMostrar, datosMostrar)}
          </>
        )}

        {estadisticas.intervaloEntreRondas && (
          <>
            {crearTitulo('Análisis de Intervalos entre Rondas')}
            
            <table id="table" className="tabla-responsive">
              <thead>
                <tr>
                  <th>Intervalo</th>
                  <th>Frecuencia</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(estadisticas.intervaloEntreRondas)
                  .sort((a, b) => {
                    const orden = {
                      '0-15 min': 1,
                      '15-30 min': 2,
                      '30-60 min': 3,
                      '1-2 horas': 4,
                      '2-4 horas': 5,
                      '4+ horas': 6
                    };
                    return orden[a[0]] - orden[b[0]];
                  })
                  .map(([intervalo, cantidad], index) => (
                    <tr key={index}>
                      <td>{intervalo}</td>
                      <td>{cantidad}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <br />
          </>
        )}

        {estadisticas.usuariosPorDia && estadisticas.porDia && (
          <>
            {crearTitulo('Actividad Diaria Detallada')}
            
            <h3>Resumen de Actividad (Últimos 30 días)</h3>
            <table id="table" className="tabla-responsive">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Rondas</th>
                  <th>Usuarios Activos</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(estadisticas.porDia)
                  .sort((a, b) => b[0].localeCompare(a[0]))
                  .slice(0, 30)
                  .map(([fecha, rondas], index) => (
                    <tr key={index}>
                      <td>{fecha}</td>
                      <td>{rondas}</td>
                      <td>{estadisticas.usuariosPorDia[fecha] || 0}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <br />
          </>
        )}

      </div>
    </div>
  );
};

export default AnalisisRondas;